import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Eye, 
  Share2, 
  Settings,
  FileText,
  Calculator,
  Image,
  List,
  CheckCircle
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAssessmentStore } from '../../stores/assessmentStore';
import { useAuthStore } from '../../stores/authStore';
import QuestionEditor from './QuestionEditor';
import AssessmentPreview from './AssessmentPreview';
import AssessmentSettings from './AssessmentSettings';
import SolutionSidebar from '../solutions/SolutionSidebar';
import QuestionLibrarySidebar from '../questions/QuestionLibrarySidebar';

const AssessmentEditor = ({ assessmentId, onClose }) => {
  const { user } = useAuthStore();
  const {
    currentAssessment,
    loadAssessment,
    updateAssessment,
    addQuestion,
    isLoading
  } = useAssessmentStore();

  const [activeTab, setActiveTab] = useState('questions');
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [assessmentTitle, setAssessmentTitle] = useState('');
  const [assessmentDescription, setAssessmentDescription] = useState('');
  const [showSolutionSidebar, setShowSolutionSidebar] = useState(false);
  const [showQuestionLibrary, setShowQuestionLibrary] = useState(false);
  const [currentEditingQuestion, setCurrentEditingQuestion] = useState(null);

  const handleInsertSolution = (solution) => {
    if (!currentEditingQuestion || !currentAssessment) return;
    const explanation = solution.steps
      ? solution.steps.map(s => s.content).join('\n')
      : solution.content || '';
    updateAssessment(currentAssessment.id, {
      questions: currentAssessment.questions.map(q =>
        q.id === currentEditingQuestion.id
          ? { ...q, explanation }
          : q
      )
    });
    setShowSolutionSidebar(false);
  };

  const handleInsertQuestion = (libraryQuestion) => {
    if (!currentAssessment) return;
    addQuestion(currentAssessment.id, {
      type: libraryQuestion.type || 'text',
      title: libraryQuestion.title || '',
      content: libraryQuestion.content || '',
      points: libraryQuestion.points || 1,
      options: libraryQuestion.options || [],
      correctAnswer: libraryQuestion.correctAnswer || '',
      explanation: libraryQuestion.explanation || '',
      mathContent: libraryQuestion.mathContent || null
    });
    setShowQuestionLibrary(false);
  };

  useEffect(() => {
    if (assessmentId && assessmentId !== 'new') {
      loadAssessment(assessmentId);
    }
  }, [assessmentId, loadAssessment]);

  useEffect(() => {
    if (currentAssessment) {
      setAssessmentTitle(currentAssessment.title || '');
      setAssessmentDescription(currentAssessment.description || '');
    }
  }, [currentAssessment]);

  const handleSave = async () => {
    if (!currentAssessment) return;

    setIsSaving(true);
    try {
      await updateAssessment(currentAssessment.id, {
        title: assessmentTitle,
        description: assessmentDescription
      });
    } catch (error) {
      console.error('Error saving assessment:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddQuestion = async (questionType) => {
    if (!currentAssessment) return;

    const newQuestion = {
      type: questionType,
      title: '',
      content: '',
      points: 1,
      options: questionType === 'multiple-choice' ? ['', '', '', ''] : [],
      correctAnswer: '',
      explanation: '',
      mathContent: questionType === 'math' ? '' : null
    };

    try {
      await addQuestion(currentAssessment.id, newQuestion);
    } catch (error) {
      console.error('Error adding question:', error);
    }
  };

  const questionTypes = [
    {
      type: 'text',
      label: 'Text Question',
      icon: FileText,
      description: 'Short answer or essay question'
    },
    {
      type: 'math',
      label: 'Math Question',
      icon: Calculator,
      description: 'Mathematical expression or equation'
    },
    {
      type: 'multiple-choice',
      label: 'Multiple Choice',
      icon: List,
      description: 'Question with multiple choice answers'
    },
    {
      type: 'true-false',
      label: 'True/False',
      icon: CheckCircle,
      description: 'True or false question'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <input
              type="text"
              value={assessmentTitle}
              onChange={(e) => setAssessmentTitle(e.target.value)}
              placeholder="Assessment Title"
              className="text-xl font-semibold bg-transparent border-none outline-none focus:bg-gray-50 px-2 py-1 rounded w-full"
            />
            <textarea
              value={assessmentDescription}
              onChange={(e) => setAssessmentDescription(e.target.value)}
              placeholder="Assessment description (optional)"
              className="text-sm text-gray-600 bg-transparent border-none outline-none focus:bg-gray-50 px-2 py-1 rounded w-full resize-none"
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowPreview(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </button>
            
            <button
              onClick={() => {/* Handle share */}}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </button>
            
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-sigma-600 hover:bg-sigma-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'questions', label: 'Questions', icon: List },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'flex items-center py-4 px-1 border-b-2 font-medium text-sm',
                  activeTab === tab.id
                    ? 'border-sigma-500 text-sigma-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'questions' && (
          <div className="p-6">
            {/* Add Question Section */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Question</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {questionTypes.map((questionType) => (
                  <button
                    key={questionType.type}
                    onClick={() => handleAddQuestion(questionType.type)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-sigma-300 hover:bg-sigma-50 transition-colors text-left group"
                  >
                    <div className="flex items-center mb-2">
                      <questionType.icon className="w-5 h-5 text-sigma-600 mr-2" />
                      <span className="font-medium text-gray-900">{questionType.label}</span>
                    </div>
                    <p className="text-sm text-gray-500">{questionType.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Questions List */}
            {currentAssessment?.questions && currentAssessment.questions.length > 0 ? (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Questions ({currentAssessment.questions.length})
                </h3>
                {currentAssessment.questions.map((question, index) => (
                  <QuestionEditor
                    key={question.id}
                    question={question}
                    questionNumber={index + 1}
                    assessmentId={currentAssessment.id}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <List className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h3>
                <p className="text-gray-500">Add your first question using the options above</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <AssessmentSettings 
            assessment={currentAssessment}
            onUpdate={(updates) => updateAssessment(currentAssessment.id, updates)}
          />
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <AssessmentPreview
          assessment={currentAssessment}
          onClose={() => setShowPreview(false)}
        />
      )}

      <SolutionSidebar
        isOpen={showSolutionSidebar}
        onClose={() => setShowSolutionSidebar(false)}
        onInsertSolution={handleInsertSolution}
        currentQuestion={currentEditingQuestion}
      />

      <QuestionLibrarySidebar
        isOpen={showQuestionLibrary}
        onClose={() => setShowQuestionLibrary(false)}
        onInsertQuestion={handleInsertQuestion}
        context="assessment"
        contextInfo={currentAssessment}
      />
    </div>
  );
};

export default AssessmentEditor;